import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, FileText, Printer } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { CHEAT_SHEETS } from "@/data/cheatSheets";

/* Cheat-sheet hub — mirrors /flashcards and /sections. Every sheet is free
   and doesn't require login. Encourage discovery via a short "why they help"
   subtitle beside each tile. Sheets flagged `hidden: true` are omitted here
   but remain in CHEAT_SHEET_BY_ID so a direct URL still resolves (useful when
   staging a replacement without deleting the original). */
export default function CheatSheetsHub() {
  const navigate = useNavigate();
  const visibleSheets = CHEAT_SHEETS.filter((s) => !s.hidden);
  const total = visibleSheets.reduce(
    (n, s) => n + s.sections.filter((sec) => sec.type === "table").length,
    0,
  );

  return (
    <div className="min-h-screen bg-[#F4F7F9]">
      <header className="border-b border-slate-200 bg-white/70 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/sections" className="flex items-center gap-3" data-testid="brand-logo-home">
            <BrandLogo chip />
            <span className="hidden sm:block text-[11px] text-slate-500 border-l border-slate-200 pl-3">
              Quick Reference
            </span>
          </Link>
          <Link
            to="/sections"
            data-testid="cheat-sheets-hub-back"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-[#0047AB]"
          >
            <ArrowLeft className="w-4 h-4" /> Back to simulators
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12" data-testid="cheat-sheets-hub-page">
        <div className="flex items-start gap-3 mb-3">
          <div className="mt-1 w-10 h-10 rounded-lg bg-[#0047AB] text-white flex items-center justify-center shrink-0">
            <FileText className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0047AB]">Quick reference</p>
            <h1 className="mt-1 text-3xl sm:text-4xl font-bold text-slate-900">Cheat Sheets</h1>
          </div>
        </div>
        <p className="text-base text-slate-600 max-w-2xl">
          Printable reference cards for the numbers you look up every shift — vital ranges, drug
          doses, MAC values, fluid calculations, emergency drugs and clinical pathology
          intervals. Save them, print them, or check them from your phone in the ward.
        </p>
        <p className="mt-1.5 text-sm text-slate-500">
          {visibleSheets.length} sheets · {total} reference tables · free · no login required
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
          {visibleSheets.map((s) => {
            const Icon = s.Icon;
            const tableCount = s.sections.filter((sec) => sec.type === "table").length;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => navigate(`/cheat-sheets/${s.id}`)}
                data-testid={`cheat-sheet-tile-${s.id}`}
                className="group text-left bg-white border border-slate-200 rounded-xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
                style={{ borderTopWidth: 3, borderTopColor: s.accent }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-11 h-11 rounded-lg flex items-center justify-center text-white shrink-0"
                    style={{ background: s.accent }}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <span
                    className="text-2xl font-bold tabular-nums"
                    style={{ color: s.accent }}
                  >
                    {String(s.n).padStart(2, "0")}
                  </span>
                </div>

                <h2 className="text-lg font-semibold text-slate-900">{s.title}</h2>
                <p className="text-sm text-slate-600 leading-relaxed mt-1.5 flex-1">{s.blurb}</p>

                <div className="flex items-center justify-between mt-4">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    <FileText className="w-3.5 h-3.5" />
                    {tableCount} table{tableCount === 1 ? "" : "s"}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500">
                    <Printer className="w-3.5 h-3.5" />
                    {s.layout === "a3" ? "A3" : "Printable"}
                  </span>
                  <span
                    className="text-sm font-semibold inline-flex items-center gap-1 group-hover:gap-1.5 transition-all"
                    style={{ color: s.accent }}
                  >
                    Open <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </main>
    </div>
  );
}
